
import {
  defineComponent, PropType, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import ConditionalDisplayModal from '@/components/conditional-display/ConditionalDisplayModal.vue';
import { FieldConfig, ParserFieldObject } from '@/types';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'FormFieldConditionalDisplay',

  components: {
    Button,
    ConditionalDisplayModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isModalVisible = ref(false);
    const contextElement = ref<HTMLElement | null>(null);

    const property = computed(() => props.parserValues.properties?.[0]);

    // Get the HTML element from the selector
    const getContextElement = () => {
      if (!props.parserValues.selector) return null;

      const template = getTemplateIframeDocument();
      if (!template) return null;

      // The selector is already processed with the actual ID by the parser
      return template.querySelector(props.parserValues.selector) as HTMLElement | null;
    };

    const currentSegments = computed(() => {
      if (!property.value?.value) return null;

      const rawValue = property.value.value;

      // If already an object/array, return it directly
      if (typeof rawValue === 'object' && rawValue !== null) {
        return rawValue;
      }

      // If string, parse it
      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          return parsed;
        } catch (e) {
          return null;
        }
      }

      return null;
    });

    const hasSegments = computed(() => currentSegments.value && Array.isArray(currentSegments.value) && currentSegments.value.length > 0);

    const segmentCount = computed(() => {
      if (!currentSegments.value) return 0;
      // Count only first-level blocks (AND blocks)
      return currentSegments.value.filter((blockAND: any[]) => Array.isArray(blockAND)
        && blockAND.length > 0
        && blockAND.some((block: any) => block && typeof block === 'object' && Object.keys(block).length > 0)).length;
    });

    const openModal = () => {
      contextElement.value = getContextElement();
      isModalVisible.value = true;
    };

    const handleSave = (segments: any) => {
      if (property.value) {
        property.value.value = segments && segments.length > 0 ? JSON.stringify(segments) : '';

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    const deleteConfiguration = () => {
      if (property.value) {
        // Set value to empty string to remove s-segments attribute
        property.value.value = '';

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    return {
      t,
      isModalVisible,
      contextElement,
      currentSegments,
      hasSegments,
      segmentCount,
      openModal,
      handleSave,
      deleteConfiguration,
    };
  },
});
